/**
 * Shared color semantics — the one place where color meaning is defined in JS.
 *
 * These values mirror the CSS design tokens in `src/index.css` (`:root`),
 * which are the single source of truth for Tailwind utilities and plain CSS.
 * This module serves JS-side consumers (SVG/canvas charts, inline styles)
 * that cannot read CSS variables. If a value changes, change both places.
 */

import type { RecommendedAction } from '../types/pricingIntelligence';

/** Provenance tier (Constitution §13.2). An inference is not a confirmation. */
export type ProvenanceTier = 'observed' | 'declared' | 'inferred';

/** Provenance tier → color. observed = green, declared = blue, inferred = violet. */
export const PROVENANCE_COLORS: Record<ProvenanceTier, string> = {
  observed: '#22c55e', // --provenance-observed (green-500)
  declared: '#3b82f6', // --provenance-declared (blue-500)
  inferred: '#8b5cf6', // --provenance-inferred (violet-500)
};

/**
 * Provenance tier → Tailwind class maps for component badges and timeline
 * dots.
 *
 * The single home for "which colour family a tier wears" in the UI
 * (Constitution §13.2): observed = green, declared = blue, inferred =
 * violet. "An inference is not a confirmation" — inferred is violet, never
 * the green of observed — so the mapping lives here exactly once and
 * components import these maps instead of re-declaring it. Values keep the
 * exact class expressions the components already use (opacity modifiers and
 * all), so adoption is a visual no-op apart from aligning any tier that had
 * drifted off its family (e.g. an inferred badge that was amber/purple).
 * `PROVENANCE_COLORS` above stays the source of truth for JS-side consumers
 * (SVG/canvas); these maps are the class-string expression of the same map.
 */

/** Badge classes (timeline panels, evidence panel). */
export const PROVENANCE_BADGE_CLASSES: Record<ProvenanceTier, string> = {
  observed: 'bg-green-900/40 text-green-400',
  declared: 'bg-blue-900/40 text-blue-400',
  inferred: 'bg-violet-900/40 text-violet-300',
};

/** Bordered pill expression (claims table, badge groups). */
export const PROVENANCE_PILL_CLASSES: Record<ProvenanceTier, string> = {
  observed: 'bg-green-900/40 text-green-400 border-green-700',
  declared: 'bg-blue-900/40 text-blue-400 border-blue-700',
  inferred: 'bg-violet-900/40 text-violet-400 border-violet-700',
};

/** Light-fill badge expression (order-investigation summary + milestone rows). */
export const PROVENANCE_SOFT_BADGE_CLASSES: Record<ProvenanceTier, string> = {
  observed: 'bg-green-500/20 text-green-300 border-green-500/30',
  declared: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  inferred: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
};

/** Timeline dot expression. */
export const PROVENANCE_DOT_CLASSES: Record<ProvenanceTier, string> = {
  observed: 'bg-green-400 border-green-600',
  declared: 'bg-blue-400 border-blue-600',
  inferred: 'bg-violet-400 border-violet-600',
};

/**
 * Soft badge expression for order-investigation milestone statuses that are
 * NOT provenance tiers (`missing`, `contradictory`). Shared by
 * CaseSummaryHeader and MilestoneRow so these expressions live in one place.
 * (MilestoneTimeline's borderless `missing`/`contradictory` expressions are a
 * pre-existing visual divergence and stay local to that component.)
 */
export const MILESTONE_STATUS_SOFT_BADGE_CLASSES: Record<'missing' | 'contradictory', string> = {
  missing: 'bg-muted-foreground/20 text-muted-foreground border-muted-foreground/30',
  contradictory: 'bg-destructive/20 text-destructive border-destructive/30',
};

/** Semantic status role. Mirrors the success/warning/destructive/ring tokens. */
export type StatusRole = 'success' | 'warning' | 'destructive' | 'info';

/**
 * Pricing action → status role. The semantic assignment ("push prices = good
 * = green, discount = red, hold = amber, watch = neutral") lives here exactly
 * once; the consuming components (BucketGrid, RecommendationDetail) express
 * each role as their own class shape (bordered chip vs. plain pill).
 */
export const ACTION_STATUS_ROLE: Record<RecommendedAction, StatusRole | 'neutral'> = {
  increase: 'success',
  decrease: 'destructive',
  hold: 'warning',
  review: 'neutral',
};

/** Status → color. */
export const STATUS_COLORS: Record<StatusRole, string> = {
  success: '#22c55e', // --success (green-500)
  warning: '#f59e0b', // --warning (amber-500)
  destructive: '#dc2626', // --destructive (red-600)
  info: '#3b82f6', // --ring (blue-500)
};

/**
 * Categorical chart palette — matplotlib tab10, 10 entries in stable order.
 * Categorical data only; meaningful data uses the status tokens.
 */
export const CHART_PALETTE: readonly string[] = [
  '#1f77b4', // --chart-1
  '#ff7f0e', // --chart-2
  '#2ca02c', // --chart-3
  '#d62728', // --chart-4
  '#9467bd', // --chart-5
  '#8c564b', // --chart-6
  '#e377c2', // --chart-7
  '#7f7f7f', // --chart-8
  '#bcbd22', // --chart-9
  '#17becf', // --chart-10
];
